import { useRef, useState } from 'react';
import { useResults } from '@/store/results';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import {
  getSiteSharingSignal, getPublishedLabelCount,
  type SiteSharingSignal, type SiteInfo,
} from '@/graph/modules/sites';
import { scoreReadiness, gradeLabel, type SiteScanRecord, type ReadinessResult } from './readiness';
import { Panel } from '@/components/Panel';
import { ModuleGate } from '@/components/ModuleGate';
import { SitePicker } from '@/components/SitePicker';
import { Metric, MetricGrid } from '@/components/Metric';
import { ScoreGauge } from '@/components/ScoreGauge';
import { ProgressBar } from '@/components/ProgressBar';
import { SeverityChip } from '@/components/SeverityChip';
import { ErrorNotice } from '@/components/ErrorNotice';
import { IconShare } from '@/components/icons';

type Status = 'idle' | 'scanning' | 'done' | 'error';

/**
 * C4 — Oversharing & readiness score (flagship). A progressive scan (§6): sites
 * are enumerated, then each site's sharing is checked one at a time with visible
 * progress. The admin scopes the scan size, and a per-site failure never kills the
 * run. The resulting score is shared to the exec summary (C6).
 */
export function OversharingScan() {
  const setReadiness = useResults((s) => s.setReadiness);

  const [status, setStatus] = useState<Status>('idle');
  const [sites, setSites] = useState<SiteInfo[]>([]);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState<ReadinessResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const cancelRef = useRef(false);

  async function runScan() {
    cancelRef.current = false;
    setStatus('scanning');
    setResult(null);
    setError(null);
    setDone(0);
    setTotal(0);

    try {
      const labelCount = await getPublishedLabelCount();
      setTotal(sites.length);

      const records: SiteScanRecord[] = [];
      for (const site of sites) {
        if (cancelRef.current) break;
        let signal: SiteSharingSignal = { anonymousLinks: 0, orgWideLinks: 0 };
        try {
          signal = await getSiteSharingSignal(site.id);
        } catch {
          // Per-site read failure (e.g. a site the admin can't read) — skip, don't abort.
        }
        records.push({ site, signal });
        setDone(records.length);
      }

      const res = scoreReadiness(records, labelCount, 'search');
      setResult(res);
      setReadiness(res);
      setStatus('done');
    } catch (e) {
      setError(e);
      setStatus('error');
    }
  }

  function cancel() {
    cancelRef.current = true;
  }

  return (
    <Panel id="c4" icon={<IconShare />} title="Oversharing & readiness score" cap="C4"
      why={CAPABILITY_SCOPES.C4.why}>
      <ModuleGate cap="C4">
        <h3 className="metric__label">Choose which sites to scan</h3>
        <SitePicker selected={sites} onChange={setSites} disabled={status === 'scanning'} />

        <div className="gate__cta" style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn--primary" onClick={() => void runScan()}
            disabled={status === 'scanning' || sites.length === 0}>
            {status === 'scanning'
              ? 'Scanning…'
              : result
                ? 'Re-scan'
                : `Scan ${sites.length} selected site${sites.length === 1 ? '' : 's'}`}
          </button>
          {status === 'scanning' && (
            <button className="btn btn--ghost" onClick={cancel}>Stop</button>
          )}
        </div>
        {sites.length === 0 && status !== 'scanning' && (
          <p className="small muted" style={{ marginTop: 8 }}>Search and select at least one site to scan.</p>
        )}

        {status === 'scanning' && (
          <div style={{ marginTop: 16 }}>
            <ProgressBar value={done} max={total} label="Checking site sharing" />
          </div>
        )}

        {status === 'error' && <ErrorNotice error={error} />}

        {result && (
          <>
            <div className="gauge" style={{ marginTop: 18 }}>
              <ScoreGauge score={result.score} />
              <div className="gauge__body">
                <p className="gauge__grade">{gradeLabel(result.score)}</p>
                <p className="gauge__caption">
                  Scored across {result.sitesScanned} selected site{result.sitesScanned === 1 ? '' : 's'}.
                  {result.labelCount != null && ` ${result.labelCount} sensitivity label${result.labelCount === 1 ? '' : 's'} published.`}
                </p>
              </div>
            </div>

            <MetricGrid>
              <Metric label="Sites scanned" value={result.sitesScanned} />
              <Metric label="Anyone links" value={result.sitesWithAnonymous}
                tone={result.sitesWithAnonymous > 0 ? 'red' : 'green'} sub="Sites with anonymous links" />
              <Metric label="Org-wide links" value={result.sitesWithOrgWide}
                tone={result.sitesWithOrgWide > 0 ? 'red' : 'green'} sub="Sites shared org-wide" />
              <Metric label="Labels published" value={result.labelCount ?? '—'}
                tone={result.labelCount === 0 ? 'red' : 'default'} sub="Purview sensitivity labels" />
            </MetricGrid>

            <h3 className="metric__label" style={{ marginTop: 8 }}>Findings</h3>
            <table className="matrix">
              <thead>
                <tr><th>Severity</th><th>Site</th><th>Detail</th></tr>
              </thead>
              <tbody>
                {result.findings.map((f) => (
                  <tr key={f.id}>
                    <td><SeverityChip severity={f.severity} /></td>
                    <td className="matrix__label">{f.title}</td>
                    <td className="matrix__detail">{f.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {result.sitesWithAnonymous + result.sitesWithOrgWide > 0 && (
              <div className="notice notice--info" style={{ marginTop: 16 }}>
                <strong>Remediation is a separate, opt-in module (C7)</strong>
                <p>Restricting or removing a broadly-shared link requires the write scope, consented
                  separately and only when you opt in — with a full before/after preview and local undo log.</p>
              </div>
            )}
          </>
        )}
      </ModuleGate>
    </Panel>
  );
}
